import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { fuseAnimations } from 'app/projects/fuse/src/lib/animations';

import { FlatTreeControl } from '@angular/cdk/tree';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { TranslateService } from '@ngx-translate/core';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { AudienceRelevantExperience } from 'app/library/grow/models/audience-relevant-experience.model';
import { MaxbrainImage } from 'app/library/image-library/models/maxbrain-image.model';
import { CreateValidScheduledStreamContentBulkSuccess } from 'app/library/scheduled-stream-content/actions';
import { IScheduleStreamContentBulkActionPayload } from 'app/library/scheduled-stream-content/interfaces/schedule-stream-content-bulk.action-payload.interface';
import { ScheduledStreamContentService } from 'app/library/scheduled-stream-content/services/scheduled-stream-content.service';
import { takeUntil } from 'rxjs/operators';
import { IEngageCategoryFilters } from '../../interfaces/engage-category-filters.interface';
import { IPostingItemPicked } from '../../interfaces/posting-item-picked.interface';
import { EngageCategoryWithPosting } from '../../models/engage-category-with-posting.model';
import { PostingService } from '../../services/posting.service';

interface ICourseStructureCategoryFlatNode {
    expandable: boolean;
    id: string;
    name: string;
    type: string;
    postingId: string;
    title: string;
    description: string;
    url: string | null;
    image: MaxbrainImage | null;
    level: number;
    isSelected: boolean;
}

@Component({
    selector: 'app-category-single-card-page',
    templateUrl: './category-single-card-page.component.html',
    styleUrls: ['./category-single-card-page.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: fuseAnimations,
})
export class CategorySingleCardPageComponent extends UnsubscribeOnDestroy implements OnInit {
    @Input()
    audienceRelevantExperiences: AudienceRelevantExperience[] = [];

    @Input()
    audienceId: string;

    @Input()
    featuredStreamContentId: string;

    @Input()
    selectedContentTypeId: string;

    @Input()
    pickOnly = false;

    @Input()
    categoryFilters: IEngageCategoryFilters = {};

    @Output()
    closeDialog = new EventEmitter<boolean>();

    @Output()
    itemPicked = new EventEmitter<IPostingItemPicked>();

    fetchCourseStructureCategoriesDataAsTree = false;
    selectedCategories: ICourseStructureCategoryFlatNode[] = [];
    isLoading: boolean;

    private _transformer = (node: EngageCategoryWithPosting, level: number) => {
        const posting = node.category.posting;

        return {
            expandable: !!node.children && node.children.length > 0,
            id: node.category.id,
            name: node.category.name,
            type: node.category.type,
            postingId: posting.id,
            title: posting.translation?.title || node.category.name,
            description: posting.translation?.description || '',
            url: posting.url,
            image: posting.image,
            isSelected: node.category.isSelected,
            level: level,
        };
    };

    treeControl = new FlatTreeControl<ICourseStructureCategoryFlatNode>(
        (node) => node.level,
        (node) => node.expandable
    );

    treeFlattener = new MatTreeFlattener(
        this._transformer,
        (node) => node.level,
        (node) => node.expandable,
        (node) => node.children
    );

    dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    constructor(
        private _store: Store,
        private _action$: Actions,
        private _postingService: PostingService,
        private _featuredStreamContentService: ScheduledStreamContentService,
        private _matSnackBar: MatSnackBar,
        private _translateService: TranslateService
    ) {
        super();
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        this._loadTreeData();

        this._action$
            .pipe(ofActionDispatched(CreateValidScheduledStreamContentBulkSuccess), takeUntil(this._unsubscribeAll))
            .subscribe((action: CreateValidScheduledStreamContentBulkSuccess) => {
                const bulk = action.payload;

                const createdCount = bulk.created.length;
                const failedCount = bulk.failed.length;

                let message: string;

                if (createdCount > 0) {
                    message = this._translateService.instant('ENGAGE.SCHEDULED_STREAM_CONTENT.MESSAGE.SCHEDULE_SUCCESS', {
                        createdCount,
                        failedCount,
                    });
                } else {
                    message = this._translateService.instant('ENGAGE.SCHEDULED_STREAM_CONTENT.MESSAGE.SCHEDULE_FAILED');
                }

                const errorTranslationMap: Record<string, string> = {
                    time_interval_overlaps_with_another_ScheduledStreamContent_interval: 'ENGAGE.SCHEDULED_STREAM_CONTENT.MESSAGE.SCHEDULE_FAILED.ERROR',
                };

                const groupedErrors = bulk.failed.reduce((acc, item) => {
                    const msg = item.message;

                    if (!acc[msg]) {
                        acc[msg] = 0;
                    }
                    acc[msg] += 1;

                    return acc;
                }, {} as Record<string, number>);

                const errorLines = Object.entries(groupedErrors)
                    .map(([msg, count]) => `${this._translateService.instant(errorTranslationMap[msg] || msg)} (${count} items)`)
                    .join('\n');

                if (failedCount > 0) {
                    message += `\n\n${errorLines}`;
                }

                this._matSnackBar.open(message, 'OK', {
                    verticalPosition: 'top',
                    duration: 5000,
                });

                this.isLoading = false;
                this.closeDialog.emit();
            });
    }

    private async _loadTreeData(): Promise<void> {
        this.dataSource.data = await this._postingService.fetchEngageCategoriesWithPostings(this.audienceId, this.categoryFilters);

        this.isLoading = false;
    }

    hasChild = (_: number, node: ICourseStructureCategoryFlatNode) => node.expandable;

    isNodeExpandable(node: ICourseStructureCategoryFlatNode): boolean {
        // If subcategories, expandable only if children exist
        if (node.type === 'sub_categories') {
            return node.expandable;
        }

        return false;
    }

    expandNode(node: ICourseStructureCategoryFlatNode, event: MouseEvent): void {
        if (!this.isNodeExpandable(node)) {
            event.stopPropagation();
            return;
        }
    }

    navigateToChangeExperience(): void {
        this._store.dispatch(new Navigate([`/admin/grow/audiences/${this.audienceId}/relevant-experiences`]));

        this.closeDialog.emit(true);
    }

    onSelectPostingStream(categoryWithPosting: ICourseStructureCategoryFlatNode): void {
        if (this.pickOnly && this.selectedCategories.length) {
            return;
        }

        categoryWithPosting.isSelected = true;
        this.selectedCategories = [...this.selectedCategories, categoryWithPosting];
    }

    onDeselectPostingStream(categoryWithPosting: ICourseStructureCategoryFlatNode): void {
        categoryWithPosting.isSelected = false;
        this.selectedCategories = this.selectedCategories.filter((p) =>
            categoryWithPosting.postingId ? p.postingId !== categoryWithPosting.postingId : p.id !== categoryWithPosting.id
        );
    }

    removeFromList(categoryWithPosting: ICourseStructureCategoryFlatNode): void {
        this.onDeselectPostingStream(categoryWithPosting);
    }

    onClearSelection(): void {
        this.selectedCategories = [];

        this.treeControl.dataNodes.forEach((node) => {
            node.isSelected = false;
        });

        this.treeControl.dataNodes = [...this.treeControl.dataNodes];
    }

    save(): void {
        this.isLoading = true;

        if (this.pickOnly) {
            const first = this.selectedCategories[0];

            if (first) {
                this.itemPicked.emit({
                    postingId: first.postingId ? Number(first.postingId) : null,
                    url: first.url ?? null,
                    title: first.title || first.name || '',
                    description: first.description || '',
                    image: first.image ?? null,
                });
            }

            this.isLoading = false;
            return;
        }

        const data: IScheduleStreamContentBulkActionPayload = {
            audience: { id: this.audienceId },
            featuredStream: { id: this.featuredStreamContentId },
            postingIds: this.selectedCategories.map((postingStream) => Number(postingStream.postingId)),
        };

        this._featuredStreamContentService.createScheduledStreamContentBulk(data);
    }
}
